/**
 * AspectRatioContainer's semantic rule. `NOTIFICATION_SORT_CHILDREN`
 * (aspect_ratio_container.cpp:109-115) skips a direct TextureRect child with a
 * proportional expand_mode and prints `WARN_PRINT_ONCE`. Only a direct child is
 * checked, matching the engine's `get_child(i)` scan.
 */
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { armEmits, groundedArm, reportArm } from '../../../../linter/ruleArms.js';
import { hiddenOrUnknowableInTree } from '../../../../linter/parentType.js';
import { ruleInt } from '../../../../godot/variant/intSlots.js';
import { boolSlotValue } from '../../../../godot/variant/variantBool.js';
import { TextureRectExpandMode } from '../../../../godot/scene/gui';
const arms = {
    unsupportedExpandMode: groundedArm('aspectratiocontainer-unsupported-texturerect-expand-mode', {
        kind: 'engine-inert',
        at: 'aspect_ratio_container.cpp:113',
        unused: 'the sort pass skips the child instead of positioning it',
    }),
};
const UNSUPPORTED_EXPAND_MODES = new Set([
    TextureRectExpandMode.EXPAND_FIT_WIDTH_PROPORTIONAL,
    TextureRectExpandMode.EXPAND_FIT_HEIGHT_PROPORTIONAL,
]);
function checkAspectRatioContainer(context) {
    const diagnostics = [];
    const { node } = context;
    for (const child of node.children) {
        if (child.type !== 'TextureRect')
            continue;
        // `as_sortable_control` runs with the default VISIBLE_IN_TREE mode (aspect_ratio_container.cpp:104,
        // container.h:50), so container.cpp:143-153 drops a top-level or hidden child before
        // `expand_mode` is read. `unknowable` counts as silent: an instanced ancestor's
        // `visible` is not in this file.
        if (boolSlotValue(child.rawProperties.top_level) === true)
            continue;
        if (hiddenOrUnknowableInTree(context.scene, child))
            continue;
        const raw = child.rawProperties.expand_mode;
        if (raw === undefined)
            continue;
        const expandMode = ruleInt(raw);
        if (expandMode === null || !UNSUPPORTED_EXPAND_MODES.has(expandMode))
            continue;
        reportArm(diagnostics, arms.unsupportedExpandMode, node, `TextureRect '${child.name}' has expand_mode ${expandMode}, a proportional mode AspectRatioContainer does not support: Godot's own sort skips positioning it ("Proportional TextureRect is currently not supported inside AspectRatioContainer"). Use a non-proportional expand_mode or a plain Container.`);
    }
    return diagnostics;
}
const aspectRatioContainerRule = {
    meta: {
        name: 'valid-aspectratiocontainer-children',
        description: 'Flags a direct TextureRect child whose proportional expand_mode AspectRatioContainer skips positioning at runtime',
        category: 'validation',
        applicableNodeTypes: ['AspectRatioContainer'],
        emits: armEmits(arms),
    },
    check: checkAspectRatioContainer,
};
ruleRegistry.register(aspectRatioContainerRule);
//# sourceMappingURL=linter.js.map